// audio/AudioContext.tsx
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import AudioManager from '../audio/manager';
import { sendEvent } from '../actions/analytics';
import { RADIO_STATION } from '../config';

const radio = `https://radio.liv.corsica/listen/${RADIO_STATION}/radio.mp3`;

const DEFAULT_TRACK = {
  title: 'Radio LIV',
  artist: 'LIV',
  album: 'Live Stream',
  artwork: [],
};

const AudioCtx = createContext(null);

export const AudioProvider = ({ children }) => {
  const [audioState, setAudioState] = useState(() => AudioManager.getState());
  const [currentTrack, setCurrentTrack] = useState(DEFAULT_TRACK);

  // Subscribe to AudioManager state
  useEffect(() => {
    return AudioManager.subscribe(setAudioState);
  }, []);

  // Intent / derived state from the manager
  const hasRadio = audioState.radioWanted;
  const isRadioPlaying = audioState.isRadioPlaying;
  const isMessagePlaying = audioState.isMessagePlaying;
  const currentUrl = audioState.currentUrl;

  // SSE connection for live radio data
  // Use radioWanted instead of "actually emitting sound right now",
  // so we keep metadata alive if a message temporarily interrupts the radio.
  useEffect(() => {
    if (!hasRadio) {
      setCurrentTrack(DEFAULT_TRACK);
      return;
    }

    const sseBaseUri = 'https://radio.liv.corsica/api/live/nowplaying/sse';
    const sseUriParams = new URLSearchParams({
      cf_connect: JSON.stringify({
        subs: {
          [`station:${RADIO_STATION}`]: { recover: true },
        },
      }),
    });

    const sseUri = `${sseBaseUri}?${sseUriParams.toString()}`;
    const eventSource = new EventSource(sseUri);

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        const npData = data?.pub?.data?.np;

        if (!npData?.station || !npData?.now_playing) {
          return;
        }

        if (npData.station.shortcode !== RADIO_STATION) {
          return;
        }

        const station = npData.station;
        const nowPlaying = npData.now_playing;
        const song = nowPlaying.song;

        if (!song) {
          return;
        }

        const nextTrack = {
          title: song.title || song.text || 'Radio LIV',
          artist: song.artist || station.name || 'LIV',
          album: song.album || nowPlaying.playlist || 'Live Stream',
          artwork: song.art
            ? [
                { src: song.art, sizes: '300x300', type: 'image/jpeg' },
                { src: song.art, sizes: '512x512', type: 'image/jpeg' },
              ]
            : [
                { src: '/favicon-96x96.png', sizes: '96x96', type: 'image/png' },
                { src: '/favicon-128x128.png', sizes: '128x128', type: 'image/png' },
                { src: '/favicon-256x256.png', sizes: '256x256', type: 'image/png' },
              ],
        };

        setCurrentTrack(nextTrack);
      } catch (error) {
        console.error('Error parsing SSE data:', error);
      }
    };

    eventSource.onerror = (error) => {
      console.error('SSE connection error:', error);
    };

    return () => {
      eventSource.close();
    };
  }, [hasRadio]);

  // Keep MediaSession metadata up to date while radio is enabled
  useEffect(() => {
    if (!hasRadio || !('mediaSession' in navigator)) {
      return;
    }

    navigator.mediaSession.metadata = new MediaMetadata({
      title: currentTrack.title,
      artist: currentTrack.artist,
      album: currentTrack.album,
      artwork: currentTrack.artwork,
    });
  }, [hasRadio, currentTrack]);

  // MediaSession handlers
  useEffect(() => {
    if (!('mediaSession' in navigator)) {
      return;
    }

    if (hasRadio) {
      navigator.mediaSession.setActionHandler('play', () => {
        AudioManager.playRadio(radio);
        sendEvent('radio_on', {});
      });

      navigator.mediaSession.setActionHandler('pause', () => {
        AudioManager.stopRadio();
        sendEvent('radio_pause', {});
      });

      navigator.mediaSession.setActionHandler('stop', () => {
        AudioManager.stopRadio();
        sendEvent('radio_off', {});
      });
    } else {
      navigator.mediaSession.setActionHandler('play', null);
      navigator.mediaSession.setActionHandler('pause', null);
      navigator.mediaSession.setActionHandler('stop', null);
      navigator.mediaSession.metadata = null;
    }

    return () => {
      navigator.mediaSession.setActionHandler('play', null);
      navigator.mediaSession.setActionHandler('pause', null);
      navigator.mediaSession.setActionHandler('stop', null);
    };
  }, [hasRadio]);

  // Reflect actual playback state to MediaSession
  useEffect(() => {
    if (!('mediaSession' in navigator)) {
      return;
    }

    if (isMessagePlaying || isRadioPlaying) {
      navigator.mediaSession.playbackState = 'playing';
      return;
    }

    if (hasRadio) {
      navigator.mediaSession.playbackState = 'paused';
      return;
    }

    navigator.mediaSession.playbackState = 'none';
  }, [hasRadio, isRadioPlaying, isMessagePlaying]);

  const playRadio = () => {
    const wasOff = !audioState.radioWanted;
    AudioManager.playRadio(radio);

    if (wasOff) {
      sendEvent('radio_on', {});
    }
  };

  const pauseRadio = () => {
    AudioManager.pauseRadio();
    sendEvent('radio_pause', {});
  };

  const stopRadio = () => {
    AudioManager.stopRadio();
    sendEvent('radio_off', {});
  };

  const playMessage = (url) => {
    AudioManager.playMessage(url);
    sendEvent('listen_on_demand', { url });
  };

  const stopMessage = () => {
    AudioManager.stopMessage();
    sendEvent('stop_listen_on_demand');
  };

  const toggleRadio = () => {
    if (audioState.radioWanted) {
      AudioManager.stopRadio();
      sendEvent('radio_off', {});
    } else {
      AudioManager.playRadio(radio);
      sendEvent('radio_on', {});
    }
  };

  const value = useMemo(
    () => ({
      // exposed state
      hasRadio,
      isRadioPlaying,
      isMessagePlaying,
      currentUrl,
      currentTrack,

      // radio controls
      toggleRadio,
      playRadio,
      pauseRadio,
      stopRadio,

      // message controls
      playMessage,
      stopMessage,
      toggleMessage: (url) => AudioManager.toggleMessage(url),
      beforeRecordingStart: () => AudioManager.beforeRecordingStart(),
      afterRecordingStop: () => AudioManager.afterRecordingStop(),
    }),
    [hasRadio, isRadioPlaying, isMessagePlaying, currentUrl, currentTrack],
  );

  return <AudioCtx.Provider value={value}>{children}</AudioCtx.Provider>;
};

export function useAudio() {
  const ctx = useContext(AudioCtx);
  if (!ctx) {
    throw new Error('useAudio must be inside AudioProvider');
  }
  return ctx;
}
